import { Grid } from '@mui/material';
import { HSpacer, RoutalPalette, RoutalSelect, RoutalSelectMultiSelectOption } from '@nx-smartmonkey/ui';

import PlusIcon from '../../../assets/svg/settings/PlusIcon';
import { useRetrieveActiveProject } from '../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveActiveProjectGeoFences } from '../../../hooks/project/useRetrieveActiveProjectGeoFences';
import { useRetrieveAllSupervisors } from '../../../hooks/supervisor/useRetrieveAllSupervisors';
import { useRetrieveUserCredentials } from '../../../hooks/user/useRetrieveUserCredentials';
import { useRetrieveFlatVehicles } from '../../../hooks/vehicle/useRetrieveFlatVehicles';
import Text from '../../../react-components/Text';
import { useSearchStopsPredicates } from '../hooks/useSearchStopsPredicates';
import { getAvailableOptions } from './SearchFiltersAvailableOptions';
import { SearchFilterBoolean } from './boolean';
import { SearchFilterDate } from './date';
import { SearchFilterDateTime } from './date_time';
import { SearchFilterTypes } from './filter/SearchFilter';
import { SearchFilterGeojson, SearchFilterGeojsonOptionProps } from './geojson/SearchFilterGeojson';
import { SearchFilterList } from './list';
import { SearchFilterListOptionProps } from './list/SearchFilterList';
import { SearchFilterNumber } from './number';
import { SearchFilterObjectList, SearchFilterObjectListOptionProps } from './object_list/SearchFilterObjectList';
import { SearchFilterString } from './string';
import { SearchFilterSurvey } from './survey';
import { SearchFilterTime } from './time';

interface SearchFiltersButtonProps {
  isExampleURL?: boolean;
  onAddFilter: (
    filter:
      | SearchFilterString
      | SearchFilterBoolean
      | SearchFilterGeojson
      | SearchFilterNumber
      | SearchFilterSurvey
      | SearchFilterDate
      | SearchFilterList
      | SearchFilterObjectList
  ) => void;
}

export const SearchFiltersButton = ({ isExampleURL = false, onAddFilter }: SearchFiltersButtonProps) => {
  const { data: project } = useRetrieveActiveProject();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { data: geoFences } = useRetrieveActiveProjectGeoFences();
  const { data: projectVehicles } = useRetrieveFlatVehicles({ project });
  const { data: projectSupervisors } = useRetrieveAllSupervisors();
  const { predicates } = useSearchStopsPredicates();

  const singleFiltersType = [
    `string`,
    `boolean`,
    `id`,
    `geojson`,
    `list`,
    `object_list`,
    `survey`,
  ] as SearchFilterTypes[];
  const singleFilters = predicates
    .filter((filter) => singleFiltersType.includes(filter.type))
    .map((filter) => filter.field);

  return (
    <RoutalSelect
      id="search-filter-button"
      disabled={isExampleURL}
      style={{
        minWidth: `140px`,
        border: `none`,
      }}
      hideArrow
      popoverProps={{
        maxHeight: `800px`,
        options: getAvailableOptions(project, userCredentials, geoFences, projectVehicles, projectSupervisors).map(
          (option): RoutalSelectMultiSelectOption => {
            const alreadyUsed = singleFilters.includes(option.field);

            return {
              key: option.field,
              width: 300,
              listItemStyles: { cursor: alreadyUsed ? `default` : `pointer` },
              content: (
                <Grid
                  container
                  direction="row"
                  wrap="nowrap"
                  alignItems="center"
                  style={{ opacity: alreadyUsed ? `50%` : `` }}
                >
                  <Grid item>
                    <img src={option.iconSrc} alt="" draggable="false" width="16" />
                  </Grid>
                  <Grid item>
                    <HSpacer small />
                  </Grid>
                  <Grid item>
                    <Text style={{ cursor: alreadyUsed ? `default` : `pointer` }} intlId={option.intlId} />
                  </Grid>
                </Grid>
              ),
              onClick: () => {
                if (alreadyUsed) return;

                switch (option.type) {
                  case `string`:
                    onAddFilter(
                      new SearchFilterString({
                        field: option.field,
                        type: `string`,
                        operator: `eq`,
                        value: null,
                        iconSrc: option.iconSrc,
                        formatted: option.formatted,
                        computed: option.computed,
                      })
                    );
                    break;
                  case `boolean`:
                    onAddFilter(
                      new SearchFilterBoolean({
                        field: option.field,
                        type: `boolean`,
                        operator: `known`,
                        value: null,
                        iconSrc: option.iconSrc,
                        formatted: option.formatted,
                        computed: option.computed,
                      })
                    );
                    break;
                  case `number`:
                    onAddFilter(
                      new SearchFilterNumber({
                        field: option.field,
                        type: `number`,
                        operator: `eq`,
                        value: null,
                        iconSrc: option.iconSrc,
                        formatted: option.formatted,
                        computed: option.computed,
                      })
                    );
                    break;
                  case `survey`:
                    onAddFilter(
                      new SearchFilterSurvey({
                        field: option.field,
                        type: `number`,
                        operator: `eq`,
                        value: 3,
                        iconSrc: option.iconSrc,
                        formatted: option.formatted,
                        computed: option.computed,
                      })
                    );
                    break;
                  case `date`:
                    onAddFilter(
                      new SearchFilterDate({
                        field: option.field,
                        type: `date`,
                        operator: `eq`,
                        value: 1,
                        iconSrc: option.iconSrc,
                        formatted: option.formatted,
                        computed: option.computed,
                      })
                    );
                    break;
                  case `date_time`:
                    onAddFilter(
                      new SearchFilterDateTime({
                        field: option.field,
                        type: `date_time`,
                        operator: `eq`,
                        value: new Date().toString(),
                        iconSrc: option.iconSrc,
                        formatted: option.formatted,
                        computed: option.computed,
                      })
                    );
                    break;
                  case `time`:
                    onAddFilter(
                      new SearchFilterTime({
                        field: option.field,
                        type: `time`,
                        operator: `eq`,
                        value: new Date().toLocaleTimeString([], { hour: `2-digit`, minute: `2-digit` }),
                        iconSrc: option.iconSrc,
                        formatted: option.formatted,
                        computed: option.computed,
                      })
                    );
                    break;
                  case `list`:
                    onAddFilter(
                      new SearchFilterList({
                        field: option.field,
                        type: `list`,
                        operator: `eq`,
                        value: option.listOptions![0].value as string,
                        iconSrc: option.iconSrc,
                        formatted: option.formatted,
                        computed: option.computed,
                        listOptions: option.listOptions! as SearchFilterListOptionProps[],
                      })
                    );
                    break;
                  case `object_list`:
                    onAddFilter(
                      new SearchFilterObjectList({
                        field: option.field,
                        type: `object_list`,
                        operator: `eq`,
                        value: option.listOptions![0].value as string,
                        iconSrc: option.iconSrc,
                        formatted: option.formatted,
                        computed: option.computed,
                        listOptions: option.listOptions! as SearchFilterObjectListOptionProps[],
                      })
                    );
                    break;
                  case `geojson`:
                    onAddFilter(
                      new SearchFilterGeojson({
                        field: option.field,
                        type: `geojson`,
                        operator: `in`,
                        value: [],
                        iconSrc: option.iconSrc,
                        listOptions: option.listOptions! as SearchFilterGeojsonOptionProps[],
                        formatted: option.formatted,
                        computed: option.computed,
                      })
                    );
                    break;
                  default:
                    return;
                }
              },
            };
          }
        ),
      }}
    >
      <Grid container direction="row" justifyContent="center" alignItems="center" wrap="nowrap">
        <PlusIcon color={RoutalPalette.secondary60} />
        <HSpacer small />
        <Text intlId="search_filters.add" style={{ cursor: `pointer`, color: RoutalPalette.secondary60 }} />
      </Grid>
    </RoutalSelect>
  );
};
